import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { GiftCardBase, Product } from "../../../utils";
import { CustomSnackbar } from "../../shared";
import { getProducts } from "utils/api/private/ProductApiService";

const initialGiftCardState: GiftCardBase = {
  id: "",
  fullname: "",
  productId: "",
  cost: "",
  code: "",
  status: "available",
  isActive: true,
};

interface AdminGiftCardEntryProps {
  open: boolean;
  onClose: () => void;
  onSave: (giftCard: GiftCardBase) => void;
  currentGiftCard?: GiftCardBase | null;
}

export const AdminGiftCardEntry: React.FC<AdminGiftCardEntryProps> = ({
  open,
  onClose,
  onSave,
  currentGiftCard,
}) => {
  const [giftCard, setGiftCard] = useState(initialGiftCardState);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // State for pagination and search
  const [totalProducts, setTotalProducts] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage] = useState("");
  const [snackbarSuccess] = useState(true);

  // Effect to update gift card form state
  useEffect(() => {
    if (currentGiftCard) {
      setGiftCard(currentGiftCard);
    } else {
      setGiftCard(initialGiftCardState);
    }
  }, [currentGiftCard]);

  // Debounce search input to avoid excessive API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      // Reset to page 0 when search query changes
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);


   const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: debouncedSearchQuery || undefined,
      };
      // Use the new paginated getProducts method
      const response = await getProducts(params);
      setProducts(response.data);
      setTotalProducts(response.meta.total);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      // Optionally, set an error message in a snackbar
    } finally {
      setLoading(false);
    }
    // 3. Add the dependencies that fetchProducts uses
  }, [paginationModel, debouncedSearchQuery]);

   useEffect(() => {
    if (open) {
      fetchProducts();
    }
    // 4. Update the useEffect dependency array
  }, [open, fetchProducts]);
  
  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) => {
    const { name, value } = e.target;
    setGiftCard((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSave = () => {
    onSave(giftCard);
  };

  const handleProductSelect = (productId: string) => {
    setGiftCard((prevState) => ({
      ...prevState,
      productId,
    }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPaginationModel({
      page: 0, // Reset to first page
      pageSize: parseInt(event.target.value, 10),
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {currentGiftCard
          ? "Modifier la carte-cadeau"
          : "Ajouter une carte-cadeau"}
      </DialogTitle>
      <DialogContent>
        {/* Gift Card Fields */}
        <TextField
          margin="dense"
          label="Nom de la carte & valeur"
          type="text"
          fullWidth
          name="fullname"
          value={giftCard.fullname}
          onChange={handleInputChange}
        />
        <TextField
          margin="dense"
          label="Code de la carte-cadeau"
          type="text"
          fullWidth
          name="code"
          value={giftCard.code}
          onChange={handleInputChange}
        />
        <TextField
          margin="dense"
          label="Coût en $"
          type="text"
          fullWidth
          name="cost"
          value={giftCard.cost}
          onChange={handleInputChange}
        />
        <FormControl fullWidth margin="dense">
          <InputLabel>Status</InputLabel>
          <Select
            name="status"
            value={giftCard.status}
            label="Status"
            onChange={handleInputChange}
          >
            <MenuItem value={"available"}>Available</MenuItem>
            <MenuItem value={"sold"}>Sold</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Sélectionner le produit
        </Typography>

        {/* Search Input */}
        <TextField
          margin="dense"
          label="Rechercher un produit (par nom, catégorie...)"
          type="search"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
        />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Prix</TableCell>
                <TableCell>Devise</TableCell>
                <TableCell>Pays</TableCell>
                <TableCell>Sélectionner</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : products.length > 0 ? (
                products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>{product.price}</TableCell>
                    <TableCell>{product.currency}</TableCell>
                    <TableCell>{product.country}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color={
                          product.id === giftCard.productId
                            ? "success"
                            : "primary"
                        }
                        onClick={() => handleProductSelect(product.id)}
                      >
                        {product.id === giftCard.productId
                          ? "Sélectionné"
                          : "Sélectionner"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Aucun produit trouvé
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        <TablePagination
          component="div"
          count={totalProducts}
          page={paginationModel.page}
          onPageChange={handlePageChange}
          rowsPerPage={paginationModel.pageSize}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Produits par page :"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleSave} color="primary">
          Sauvegarder
        </Button>
      </DialogActions>
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        isSuccess={snackbarSuccess}
        handleClose={handleSnackbarClose}
      />
    </Dialog>
  );
};
